import React, { useState } from 'react';
import {
    HiOutlineDocumentText as DescriptionIcon,
    HiOutlineTrash,
    HiOutlineArrowUp,
    HiOutlineArrowDown,
    HiOutlineChevronDoubleUp,
    HiOutlineChevronDoubleDown,
    HiOutlineSortAscending as ReorderIcon,
    HiOutlineDocumentText,
} from 'react-icons/hi';
import { FaAsterisk as RequiredIcon } from 'react-icons/fa6';
import { FieldItemController } from './fieldItemInterface';
import { FieldItemViewProps } from './fieldItemProps';
import { ReportingControllerBaseConfig } from '../../../../base';
import { FieldTypeItem } from '../../type';

export function createFieldItemController(
    config: ReportingControllerBaseConfig & {
        typeOptions: FieldTypeItem[];
    }
): FieldItemController {
    return {
        useProps(item, props): FieldItemViewProps {
            const field = props.value;
            const { index, totalFields, isFirst, isLast } = props;

            const reorderField = (toIndex: number) => {
                props.onReorder(toIndex);
            };

            const reorderEnumOptions = (
                draggedId: string,
                targetId: string,
                insertBefore: boolean
            ) => {
                const currentOptions = field.options || [];
                const draggedIndex = currentOptions.findIndex(
                    (opt) => opt._local_id === draggedId
                );
                const targetIndex = currentOptions.findIndex(
                    (opt) => opt._local_id === targetId
                );

                if (draggedIndex === -1 || targetIndex === -1) return;

                const newOptions = [...currentOptions];
                const [draggedOption] = newOptions.splice(draggedIndex, 1);

                // Calculate the insertion index
                let insertIndex = targetIndex;
                if (!insertBefore && draggedIndex < targetIndex) {
                    insertIndex = targetIndex;
                } else if (!insertBefore && draggedIndex > targetIndex) {
                    insertIndex = targetIndex + 1;
                } else if (insertBefore && draggedIndex > targetIndex) {
                    insertIndex = targetIndex;
                } else if (insertBefore && draggedIndex < targetIndex) {
                    insertIndex = targetIndex - 1;
                }

                newOptions.splice(insertIndex, 0, draggedOption);

                props.onChange({
                    ...field,
                    options: newOptions,
                });
            };

            return {
                id: field._local_id,
                label: field.title ?? '',
                type: field.type,
                required: field.required,
                description: field.description,
                showDescription: field.showDescription,
                field: {
                    title: {
                        input: {
                            value: field.title ?? '',
                            onChange(event) {
                                props.onChange({ ...field, title: event.target.value });
                            },
                        },
                        ref: props.titleInputRef,
                    },
                    description: field.showDescription
                        ? {
                              input: {
                                  value: field.description ?? '',
                                  onChange(event) {
                                      props.onChange({
                                          ...field,
                                          description: event.target.value,
                                      });
                                  },
                              },
                          }
                        : null,
                    type: {
                        constraints:
                            field.type === 'enum'
                                ? { label: 'Respondents can select a single option' }
                                : null,
                        button: {
                            onClick() {
                                // Menu button click is handled by Chakra UI Menu component
                            },
                        },
                        current: (() => {
                            const currentTypeOption = config.typeOptions.find(
                                (option) => option.value === field.type
                            );
                            return currentTypeOption
                                ? {
                                      label: currentTypeOption.displayName,
                                      Icon: currentTypeOption.Icon,
                                  }
                                : {
                                      label: 'Select type',
                                      Icon: HiOutlineDocumentText,
                                  };
                        })(),
                        options: config.typeOptions.map((option) => ({
                            label: option.displayName,
                            Icon: option.Icon,
                            onClick: () => {
                                props.onChange({
                                    ...field,
                                    type: option.value,
                                });
                            },
                            id: option.value,
                            value: option.value,
                        })),
                    },
                    options:
                        field.type === 'enum'
                            ? {
                                  items: (field.options || []).map((option) => {
                                      const canRemove = (field.options || []).length > 1;
                                      return {
                                          id: option._local_id,
                                          value: option.displayName,
                                          label: option.displayName,
                                          isDragOver: false,
                                          insertPosition: undefined,
                                          canRemove,
                                          input: {
                                              value: option.displayName,
                                              onChange: (event) => {
                                                  const updatedOptions = (
                                                      field.options || []
                                                  ).map((opt) =>
                                                      opt._local_id === option._local_id
                                                          ? {
                                                                ...opt,
                                                                displayName:
                                                                    event.target.value,
                                                                key: event.target.value,
                                                            }
                                                          : opt
                                                  );
                                                  props.onChange({
                                                      ...field,
                                                      options: updatedOptions,
                                                  });
                                              },
                                          },
                                          onRemove: () => {
                                              // Only allow removal if there's more than one option
                                              if ((field.options || []).length > 1) {
                                                  const updatedOptions = (
                                                      field.options || []
                                                  ).filter(
                                                      (opt) =>
                                                          opt._local_id !==
                                                          option._local_id
                                                  );
                                                  props.onChange({
                                                      ...field,
                                                      options: updatedOptions,
                                                  });
                                              }
                                          },
                                          onDragStart: (event) => {
                                              event.dataTransfer.effectAllowed = 'move';
                                              event.dataTransfer.setData(
                                                  'text/plain',
                                                  option._local_id
                                              );
                                          },
                                          onDragOver: (event) => {
                                              event.preventDefault();
                                              event.dataTransfer.dropEffect = 'move';
                                          },
                                          onDrop: (event, insertBefore) => {
                                              event.preventDefault();
                                              const draggedId =
                                                  event.dataTransfer.getData(
                                                      'text/plain'
                                                  );

                                              if (
                                                  draggedId &&
                                                  draggedId !== option._local_id
                                              ) {
                                                  reorderEnumOptions(
                                                      draggedId,
                                                      option._local_id,
                                                      insertBefore ?? false
                                                  );
                                              }
                                          },
                                          onDragLeave: (event) => {
                                              // Handled by member item controller
                                          },
                                          onDragEnd: (event) => {
                                              // Handled by member item controller
                                          },
                                      };
                                  }),
                                  onAdd: () => {
                                      const newOption = {
                                          _local_id: crypto.randomUUID(),
                                          id: undefined,
                                          displayName: `Option ${(field.options || []).length + 1}`,
                                      };
                                      const updatedOptions = [
                                          ...(field.options || []),
                                          newOption,
                                      ];
                                      props.onChange({
                                          ...field,
                                          options: updatedOptions,
                                      });
                                  },
                              }
                            : null,
                },
                menu: {
                    popover: {},
                    variant: 'compact',
                    header: {
                        label: 'Field options',
                    },
                    items: [
                        {
                            kind: 'action',
                            label: 'Required',
                            Icon: RequiredIcon,
                            action: {
                                kind: 'switch',
                                switch: {
                                    isChecked: field.required,
                                    onChange(event) {
                                        props.onChange({
                                            ...field,
                                            required: event.target.checked,
                                        });
                                    },
                                },
                            },
                        },
                        {
                            kind: 'action',
                            label: 'Description',
                            Icon: DescriptionIcon,
                            action: {
                                kind: 'switch',
                                switch: {
                                    isChecked: field.showDescription,
                                    onChange(event) {
                                        props.onChange({
                                            ...field,
                                            showDescription: event.target.checked,
                                        });
                                    },
                                },
                            },
                        },
                        // Reordering options grouped in a submenu - only show if more than 1 field
                        ...(totalFields > 1
                            ? [
                                  {
                                      kind: 'divider' as const,
                                  },
                                  {
                                      kind: 'folder' as const,
                                      label: 'Move question',
                                      Icon: ReorderIcon,
                                      items: [
                                          // Move up (only if not first)
                                          ...(isFirst
                                              ? []
                                              : [
                                                    {
                                                        kind: 'action' as const,
                                                        label: 'Move up',
                                                        Icon: HiOutlineArrowUp,
                                                        action: {
                                                            kind: 'button' as const,
                                                            onClick() {
                                                                reorderField(index - 1);
                                                            },
                                                        },
                                                    },
                                                ]),
                                          // Move down (only if not last)
                                          ...(isLast
                                              ? []
                                              : [
                                                    {
                                                        kind: 'action' as const,
                                                        label: 'Move down',
                                                        Icon: HiOutlineArrowDown,
                                                        action: {
                                                            kind: 'button' as const,
                                                            onClick() {
                                                                reorderField(index + 1);
                                                            },
                                                        },
                                                    },
                                                ]),
                                          // Separator if we have both single-step and jump options
                                          ...(!isFirst && !isLast && totalFields > 2
                                              ? [{ kind: 'divider' as const }]
                                              : []),
                                          // Move to top (only if not first and more than 2 fields)
                                          ...(isFirst || totalFields <= 2
                                              ? []
                                              : [
                                                    {
                                                        kind: 'action' as const,
                                                        label: 'Move to top',
                                                        Icon: HiOutlineChevronDoubleUp,
                                                        action: {
                                                            kind: 'button' as const,
                                                            onClick() {
                                                                reorderField(0);
                                                            },
                                                        },
                                                    },
                                                ]),
                                          // Move to bottom (only if not last and more than 2 fields)
                                          ...(isLast || totalFields <= 2
                                              ? []
                                              : [
                                                    {
                                                        kind: 'action' as const,
                                                        label: 'Move to bottom',
                                                        Icon: HiOutlineChevronDoubleDown,
                                                        action: {
                                                            kind: 'button' as const,
                                                            onClick() {
                                                                reorderField(
                                                                    totalFields - 1
                                                                );
                                                            },
                                                        },
                                                    },
                                                ]),
                                      ],
                                  },
                              ]
                            : []),
                        {
                            kind: 'divider',
                        },
                        {
                            kind: 'action',
                            label: 'Delete',
                            Icon: HiOutlineTrash,
                            colorScheme: 'red',
                            action: {
                                kind: 'button',
                                onClick() {
                                    props.onRemove();
                                },
                            },
                        },
                    ],
                },
                insert: null, // This will need to be passed from outside
            };
        },
    };
}
